/**
 * What /settings does when no section is named.
 *
 * Sections used to be a `?tab=` parameter, and links into them are spread
 * across the schedule and course pages and sitting in people's bookmarks. Those
 * still arrive here, so this forwards them to the section's own URL, keeping
 * the year and the anchor they were carrying. Everything else gets the
 * overview.
 */
import { Navigate, useLocation, useOutletContext, useSearchParams } from 'react-router-dom';
import SettingsOverview from './SettingsOverview';
import { settingsPath, settingsSection } from './settingsSections';
import type { SettingsOutletContext } from './SettingsLayout';

export function SettingsIndexRoute() {
  const { schoolSlug, isAdmin } = useOutletContext<SettingsOutletContext>();
  const [searchParams] = useSearchParams();
  // The router owns the location here, not the browser: reading window.location
  // would miss the hash under a MemoryRouter and on any in-app navigation.
  const { hash } = useLocation();

  const legacyTab = searchParams.get('tab');
  const section = settingsSection(legacyTab ?? undefined);

  if (section && (isAdmin || !section.adminOnly)) {
    const year = searchParams.get('year');
    const search = year ? `?year=${encodeURIComponent(year)}` : '';
    return <Navigate to={`${settingsPath(schoolSlug, section.slug)}${search}${hash}`} replace />;
  }

  // A teacher or parent has exactly one section, so the overview would be a
  // list of one. Send them straight to it.
  if (!isAdmin) {
    return <Navigate to={settingsPath(schoolSlug, 'account')} replace />;
  }

  return <SettingsOverview />;
}

export default SettingsIndexRoute;
